import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { MoodboardItem } from '../types/moodboard';
interface MoodboardCardProps {
  item: MoodboardItem;
  onDelete: (id: string) => void;
  isHighlighted?: boolean;
  isDimmed?: boolean;
  onImageClick?: (id: string) => void;
  index?: number;
}
export function MoodboardCard({
  item,
  onDelete,
  isHighlighted = false,
  isDimmed = false,
  onImageClick,
  index = 0
}: MoodboardCardProps) {
  const renderContent = () => {
    switch (item.type) {
      case 'image':
        return <div className="relative w-full overflow-hidden cursor-pointer group/image" onClick={() => onImageClick?.(item.id)} data-id="element-254">
            <img src={item.content} alt={item.title || 'Moodboard image'} className="w-full h-auto object-cover transition-all duration-500 group-hover/image:opacity-90 group-hover/image:scale-105" loading="lazy" data-id="element-255" />
            <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/10 transition-colors duration-300" data-id="element-256" />
          </div>;
      case 'note':
        return <div className="px-4 sm:px-6 py-8 sm:py-10 min-h-[120px] sm:min-h-[140px] flex flex-col justify-center border-b border-border" data-id="element-257">
            <p className="text-[13px] font-sans font-normal leading-[1.8] text-[#1A1A1A]" data-id="element-258">
              "{item.content}"
            </p>
          </div>;
      case 'link':
        return <div className="px-4 sm:px-6 py-6 sm:py-8 border-b border-border" data-id="element-259">
            <p className="font-heading font-normal text-[11px] tracking-[0.2em] uppercase text-[#666] mb-3" data-id="element-260">
              {item.title || 'REFERENCE'}
            </p>
            {item.description && <p className="text-[12px] text-[#444] leading-[1.7] font-sans mb-4" data-id="element-261">
                {item.description}
              </p>}
            <a href={item.content} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-heading text-[11px] tracking-[0.15em] uppercase text-black border-b border-black pb-0.5 hover:opacity-60 transition-opacity" data-id="element-262">
              VISITA
              <ExternalLink className="w-3 h-3" data-id="element-263" />
            </a>
          </div>;
      case 'color_palette':
        const colors = item.content.split(',').map(c => c.trim()).filter(c => c.startsWith('#'));
        return <div className="p-4 sm:p-6" data-id="element-264">
            <p className="font-heading font-normal text-[10px] tracking-[0.25em] uppercase text-[#666] mb-4" data-id="element-265">
              PALETTE
            </p>
            <div className="flex w-full h-20 overflow-hidden" data-id="element-266">
              {colors.map((color, idx) => <div key={idx} className="flex-1 h-full transition-all duration-300 hover:flex-[1.5]" style={{
              backgroundColor: color
            }} title={color} data-id="element-267" />)}
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <motion.div layout initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className={`group relative flex flex-col mb-4 sm:mb-8 ${isHighlighted ? 'ring-2 ring-black ring-offset-4' : ''} ${isDimmed ? 'opacity-30 grayscale' : 'opacity-100'}`} data-id="element-268">
      <div className="overflow-hidden border border-[#E5E5E5] bg-white relative hover:border-[#999] transition-colors duration-300 rounded-lg flex-1 flex flex-col" data-id="element-269">
        {/* Delete Button */}
        <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-270">
          <button className="font-heading text-[9px] tracking-[0.15em] uppercase text-[#666] hover:text-black bg-white/90 backdrop-blur-sm px-3 py-1.5 border border-border transition-colors rounded-full" onClick={e => {
          e.stopPropagation();
          onDelete(item.id);
        }} data-id="element-271">
            RIMUOVI
          </button>
        </div>

        {renderContent()}

        <div className="px-4 sm:px-6 py-4 sm:py-5" data-id="element-272">
          {item.type !== 'note' && item.type !== 'link' && item.title && <h3 className="font-heading font-normal text-[11px] tracking-[0.2em] uppercase mb-2 text-black" data-id="element-273">
              {item.title}
            </h3>}
          {item.description && <p className="text-[12px] text-[#666] mb-4 line-clamp-3 leading-[1.6] font-sans" data-id="element-274">
              {item.description}
            </p>}

          {item.tags.length > 0 && <div className="px-4 sm:px-6 py-4 sm:py-5 border-t border-border bg-[#FAFAFA] mt-auto" data-id="element-275">
              <div className="flex flex-wrap gap-2" data-id="element-276">
                {item.tags.map((tag, idx) => <span key={idx} className="font-heading text-[9px] tracking-[0.15em] uppercase text-[#666] border border-border px-2.5 py-1 hover:text-black hover:border-[#999] transition-colors rounded-full bg-white" data-id="element-277">
                    {tag}
                  </span>)}
              </div>
            </div>}
        </div>
      </div>
    </motion.div>;
}